import { getMe, hasTenantAdminAccess } from "@shared/api/server/gs3Server";
import { User } from "@shared/api";
import { isAuthenticated } from "@modules/auth";
import { errorToast } from "@telluscompany/design-system";
import { LoaderFunctionArgs, redirect } from "react-router-dom";
import { queryClient } from "@shared/config";
import i18next from "i18next";
import type { AxiosError } from "axios";

const EXPIRATION_TIME_5_MINUTES = 5 * 60 * 1000; // 5 minutos

const ME_QUERY_OPTIONS = {
  queryKey: ["me"],
  queryFn: getMe,
  staleTime: EXPIRATION_TIME_5_MINUTES,
};

const ensureUser = () =>
  queryClient.ensureQueryData<User>(ME_QUERY_OPTIONS);

export const rootLoader = ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const urlError = url.searchParams.get("error");

  if (urlError) {
    return { error: urlError };
  }

  if (!isAuthenticated()) {
    throw redirect("/login");
  }

  return null;
};

export const privateLoader = async ({
  request: _request,
}: LoaderFunctionArgs) => {
  try {
    await ensureUser();
    return null;
  } catch (error: unknown) {
    if (
      (error as { response?: { status?: number } })?.response?.status === 401
    ) {
      return redirect("/login");
    }
    return null;
  }
};

export const ownerLoader = async () => {
  try {
    const user = await ensureUser();
    if (user.role !== "owner" && user.role !== "ownerViewer") {
      throw redirect("/");
    }

    return user;
  } catch (error: unknown) {
    // `throw redirect(...)` lança um `Response`. Sem este re-throw, o catch
    // engole a denial intencional do `if` acima e exibe um toast indevido.
    // Qualquer `Response` (incl. redirects de outros pontos) deve passar
    // limpa — só erros "de verdade" caem nas branches abaixo.
    if (error instanceof Response) {
      throw error;
    }

    // 401 = sessão expirada/ausente, não permissão negada — manda para
    // /login (sem toast), alinhado com `privateLoader` e `adminLoader`.
    if (
      (error as { response?: { status?: number } })?.response?.status === 401
    ) {
      throw redirect("/login");
    }

    const parsedMessage = (error as AxiosError | undefined)?.parsedMessage;
    errorToast({
      size: "small",
      description: parsedMessage ?? i18next.t("auth.pagePermissionDenied"),
    });
    throw redirect("/");
  }
};

// Pre-check de acesso à área Admin (configuração do tenant; sobre a distinção
// "área Admin" × "Module operacional", ver comentário em
// @shared/hooks/useAdminAccessQuery.ts). O gate real vive no backend
// (EnsureTenantAdminAccess), avaliado em cada endpoint de /v1/admin/*.
//
// O acesso é derivado de `me.tenantAccess` (já carregado no cache pelo
// ensureUser via GET /v1/user/me) — sem sondar /v1/admin/*. Contrato:
// docs/prd/admin/admin-endpoints.md §0; regras: docs/permissions.md.
export const adminLoader = async () => {
  let user: User;
  try {
    user = await ensureUser();
  } catch (error: unknown) {
    if (
      (error as { response?: { status?: number } })?.response?.status === 401
    ) {
      throw redirect("/login");
    }
    throw redirect("/");
  }

  if (hasTenantAdminAccess(user.tenantAccess)) {
    return null;
  }

  errorToast({
    size: "small",
    description: i18next.t("auth.pagePermissionDenied"),
  });
  throw redirect("/");
};
